import type { BaseIssue, BaseSchema, ErrorMessage } from "valibot";
import {
  boolean,
  check,
  custom,
  fallback,
  instance,
  integer,
  isoDate,
  isoDateTime,
  maxLength,
  maxValue,
  minLength,
  minValue,
  nonEmpty,
  nonNullish,
  nonOptional,
  nullish,
  number,
  object,
  optional,
  picklist,
  pipe,
  record,
  startsWith,
  string,
  transform,
  trim,
} from "valibot";
import { CURRENCIES, DECIMAL_MARKS, FILE_TYPES, HEADINGS, OPERATORS, PHOTO_QUALITIES } from "../../types";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import {
  GRIDFS_FILE_PREFIX,
  MAX_CHARACTERS_MD,
  MAX_CHARACTERS_PHONE_NUMBER,
  MAX_CHARACTERS_SM,
  MAX_CHARACTERS_XS,
} from "../../constants";
import { isValidEmail } from "../emailUtil";
import { isEmpty } from "lodash-es";
import parsePhoneNumberFromString from "libphonenumber-js";
import { runWithOwner } from "solid-js";
import owner from "../../owner";
import type { SelectableIconName } from "../iconUtil";
import { toIsoCurrency } from "../currencyUtil";
import ServiceAccountId from "../../state/ServiceAccountId";
import { keyFromSelector } from "i18next";
import { normalize } from "../stringUtil.ts";
import type { InvalidPlaceholder } from "../freeMarkerPlaceholderUtil";

const ISO_DATE_TIME_PATTERN = "yyyy-MM-ddTHH:mm";

const DATA_NAME_FORBIDDEN_KEYWORDS = new Set(["true", "false", "gt", "gte", "lt", "lte", "as", "in", "using"]);

const noAction = <T = unknown>() => transform<T, T>((input) => input);

export const currency = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  pipe(
    picklist(CURRENCIES, message),
    transform((v) => toIsoCurrency(v)),
  );

export const decimalMark = (message?: ErrorMessage<BaseIssue<unknown>>) => picklist(DECIMAL_MARKS, message);

export const operator = (message?: ErrorMessage<BaseIssue<unknown>>) => picklist(OPERATORS, message);

export const heading = (message?: ErrorMessage<BaseIssue<unknown>>) => picklist(HEADINGS, message);

export const fileType = (message?: ErrorMessage<BaseIssue<unknown>>) => picklist(FILE_TYPES, message);

export const photoQuality = (message?: ErrorMessage<BaseIssue<unknown>>) => picklist(PHOTO_QUALITIES, message);

export const icon = (allowedIcons: SelectableIconName[]) =>
  picklist(allowedIcons, errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_ICON)));

export const dataName = () =>
  pipe(
    string(),
    nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    check(
      (value) => !DATA_NAME_FORBIDDEN_KEYWORDS.has(value),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_VALUES)),
    ),
    check((value) => !/\s/.test(value), errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_WHITESPACE))),
    check(
      (value) => /^\p{L}/u.test(value),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_MUST_START_WITH_LETTER)),
    ),
    check(
      (value) => !/[^\p{L}0-9_]+/u.test(value),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SYMBOL_OR_EMOJI)),
    ),
    maxLength(MAX_CHARACTERS_MD, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
  );

export const label = () =>
  pipe(
    string(),
    nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    maxLength(MAX_CHARACTERS_MD, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
  );

export const labelAsName = () =>
  nullish(
    pipe(string(), maxLength(MAX_CHARACTERS_SM, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH)))),
  );

export const required = () => fallback(boolean(), false);

export const rememberInput = () => fallback(boolean(), false);

export const dataSource = () =>
  object(
    { id: string(), mapping: record(string(), boolean()) },
    errorTranslation(keyFromSelector(($) => $.VALIDATION_DATA_SOURCE)),
  );

export const serviceAccount = () =>
  instance(ServiceAccountId, errorTranslation(keyFromSelector(($) => $.VALIDATION_SERVICE_ACCOUNT)));

export const decimalValue = (min: number, max: number) =>
  pipe(
    number(errorTranslation(keyFromSelector(($) => $.VALIDATION_DECIMAL))),
    minValue(min, errorTranslation(keyFromSelector(($) => $.VALIDATION_MIN_SIZE))),
    maxValue(max, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_SIZE))),
  );

export const integerValue = (min: number, max: number) =>
  pipe(decimalValue(min, max), integer(errorTranslation(keyFromSelector(($) => $.VALIDATION_INTEGER))));

export const textValue = (min: number, max: number) =>
  pipe(
    nonOptional(string(), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    minLength(min, errorTranslation(keyFromSelector(($) => $.VALIDATION_MIN_LENGTH))),
    maxLength(max, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
  );

export const optionalTextValue = (max: number) =>
  optional(pipe(string(), maxLength(max, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH)))));

export const requireText = (max: number, shouldTrim = false) =>
  pipe(
    nonOptional(string(), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    shouldTrim ? trim() : noAction(),
    nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    maxLength(max, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
  );

// Mail clients turn URL-like text into links
const URL_PATTERN = /:\/\/|www\./i;

export const noUrl = () =>
  pipe(
    string(),
    check((value) => !URL_PATTERN.test(value), errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_URL))),
  );

// Names are printed in e-mails (e.g. invites), so only letters from any script and a few name punctuation characters
// are allowed. Digits are allowed for accounts that represent a device. Keep in sync with PersonName in api.
// ZWNJ and ZWJ (U+200C, U+200D) are required to spell names in e.g. Persian and some Indic scripts.
const PERSON_NAME = /^[\p{L}\p{M}\p{Nd}' .\-\u200C\u200D]+$/u;
const HAS_LETTER_OR_DIGIT = /[\p{L}\p{Nd}]/u;

// Phones auto-insert the curly apostrophe, store the straight one so both are found when searching
export const normalizePersonName = (name: string): string =>
  name.normalize("NFC").replace(/\s+/g, " ").trim().replaceAll("\u2019", "'");

export const personName = () =>
  pipe(
    nonOptional(string(), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    transform(normalizePersonName),
    nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    // Count code points like api does, not UTF-16 code units
    check(
      (value) => [...value].length <= MAX_CHARACTERS_XS,
      errorTranslation(
        keyFromSelector(($) => $.VALIDATION_MAX_LENGTH),
        { expected: MAX_CHARACTERS_XS },
      ),
    ),
    // An empty name is already reported by nonEmpty
    check(
      (value) =>
        isEmpty(value) || (PERSON_NAME.test(value) && HAS_LETTER_OR_DIGIT.test(value) && !URL_PATTERN.test(value)),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_INVALID_PERSON_NAME)),
    ),
  );

export const requireResource = () => string(errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_RESOURCE)));

export const resource = () => nullish(string(errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_RESOURCE))));

export const errorTranslation =
  (translationKey: TranslationKey, variables: Record<string, unknown> = {}) =>
  (issue: BaseIssue<unknown>): string =>
    // Run with owner since async validations don't have an owner when defining schemes
    runWithOwner(owner(), () => {
      const { t } = useTranslation();
      return t(translationKey, { expected: issue.requirement, input: issue.input, ...variables });
    }) ?? "";

export const email = (max: number) =>
  pipe(
    string(),
    maxLength(max, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
    check((v) => isEmpty(v) || isValidEmail(v), errorTranslation(keyFromSelector(($) => $.VALIDATION_EMAIL))),
  );

export const phoneNumber = () =>
  pipe(
    string(),
    maxLength(MAX_CHARACTERS_PHONE_NUMBER, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
    check(
      (v) => isEmpty(v) || (parsePhoneNumberFromString(v)?.isValid() ?? false),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_PHONE_NUMBER)),
    ),
  );

export const invalidPlaceholderError = ({ text, line }: InvalidPlaceholder) =>
  errorTranslation(
    keyFromSelector(($) => $.VALIDATION_INVALID_PLACEHOLDER),
    { placeholder: text, line },
  );

export const strippedIsoDateTime = () =>
  pipe(
    string(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATE_TIME))),
    transform((dateString) => dateString.substring(0, ISO_DATE_TIME_PATTERN.length)),
    isoDateTime(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATE_TIME))),
  );

export const isoDateString = () =>
  pipe(
    string(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATE))),
    isoDate(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATE))),
  );

export const require = <I, O>(
  wrapped: BaseSchema<I, O, BaseIssue<unknown>>,
  message?: ErrorMessage<BaseIssue<unknown>>,
) => nonNullish(wrapped, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY)));

export const gridFSFile = () =>
  pipe(
    string(),
    startsWith(GRIDFS_FILE_PREFIX),
    transform((value) => value.substring(GRIDFS_FILE_PREFIX.length)),
  );

export const uniqueItems =
  <T, K extends keyof T>(by: K) =>
  (items: T[]) => {
    const values = items.map((item) => item[by]);
    return values.length === new Set(values).size; // If size matches, there were no duplicates
  };

export const normalizedLiteral = (expected: string, message: ErrorMessage<BaseIssue<unknown>>) =>
  custom<string>((input) => typeof input === "string" && normalize(input) === normalize(expected), message);
